import store from 'redux/store';
import { isEmpty } from 'lodash-es';
import { INCOMING_MESSAGES } from '@rapyd/use-hcp-preview/dist/consts';

import {
  THANK_YOU_PAGE_URL,
  APP_ROUTE_NOT_FOUND,
  AWAITS_NEXT_ACTION
} from 'routes/paths';
import { displaySpinner, hideSpinner } from 'redux/actions/global';
import { disableCardAbstraction } from 'redux/actions/card';
import * as CONSTS from 'redux/constants';
import { CARD_ABSTRACION_ERROR } from 'redux/constants';
import API from 'services/api';
import i18n from 'services/i18n';
import history from 'shared/services/history';
import localStorageService from 'services/localStorage';
import showErrorAlert, { parseError } from 'services/errorHandler';
import { sendGetPageFailMessage, sendParentMessage } from 'services/iframeService';
import { getRouteWithToken } from 'utilities/routes';
import {
  redirectToError,
  downloadPdf,
  submitKortaForm,
  shouldRedirectToThankYou,
  getMerchantPassedFields,
  isDelayedPayment,
  sharePdf,
  isAwaitingNextAction,
  is3DSPending
} from 'utilities';
import { AuthorizationFlow } from 'utilities/authorizationFlow';
import { getClientDetails } from 'utilities/global';
import { trackPaymentSuccess, trackPaymentFail } from 'utilities/analytics/checkout';
import { trackCardSubmit, trackCategorySubmit } from 'utilities/analytics/demo';
import {
  ACT_CODE,
  HOSTED_PAGE_TYPES,
  HOSTED_PAGE_TYPES_TOKENS,
  FIXED_SIDE,
  CATEGORIES,
  KORTA_PAYMENT_METHOD_TYPE_NAME,
  VALITOR_PAYMENT_METHOD_TYPE_NAME,
  cardDetails,
  CASE_TYPES,
  MANDATORY_CARD_FIELDS,
  ERROR_CODE,
  SUCCESS_CODE
} from 'consts';

export const getCheckoutPageStart = () => ({
  type: CONSTS.GET_CHECKOUT_PAGE_START
});

export const getCheckoutPageSuccess = ({
  data,
  redirectToThankYou,
  authorizeFlowRedirect,
  checkoutAlreadyInUse
}) => ({
  type: CONSTS.GET_CHECKOUT_PAGE_SUCCESS,
  payload: {
    data,
    checkoutAlreadyInUse: checkoutAlreadyInUse ?? !!redirectToThankYou,
    authorizeFlowRedirect
  }
});

export const getCheckoutPageFail = (error, data) => ({
  type: CONSTS.GET_CHECKOUT_PAGE_FAIL,
  payload: { error, data }
});

export const authorizePaymentMethodStart = () => ({
  type: CONSTS.AUTHORIZE_PAYMENT_METHOD_START
});

export const authorizePaymentMethodFail = () => ({
  type: CONSTS.AUTHORIZE_PAYMENT_METHOD_FAIL
});

export const authorizePaymentMethodSuccess = () => ({
  type: CONSTS.AUTHORIZE_PAYMENT_METHOD_SUCCESS
});

export const createPaymentStart = () => ({
  type: CONSTS.CREATE_PAYMENT_START
});

export const createPaymentSuccess = data => ({
  type: CONSTS.CREATE_PAYMENT_SUCCESS,
  payload: data
});

export const createPaymentFail = ({ error, digitalWalletProvider }) => ({
  type: CONSTS.CREATE_PAYMENT_FAIL,
  payload: { error },
  meta: { digitalWalletProvider }
});

export const resetPaymentData = () => ({
  type: CONSTS.RESET_PAYMENT_DATA
});

export const setSelectedType = data => ({
  type: CONSTS.SET_SELECTED_TYPE,
  payload: data
});

export const resetSelectedType = () => ({
  type: CONSTS.RESET_SELECTED_TYPE
});

export const setSelectedCategory = category => ({
  type: CONSTS.SET_SELECTED_CATEGORY,
  payload: category
});

export const setSelectedCurrency = currency => ({
  type: CONSTS.SET_SELECTED_CURRENCY,
  payload: currency
});

export const setRequiredFields = data => ({
  type: CONSTS.SET_REQUIRED_FIELDS,
  payload: data
});

export const setRequiredFieldsLoading = data => ({
  type: CONSTS.SET_REQUIRED_FIELDS_LOADING,
  payload: data
});

export const clearRequiredFields = () => ({
  type: CONSTS.CLEAR_REQUIRED_FIELDS
});

export const setPmOptions = data => ({
  type: CONSTS.SET_PM_OPTIONS,
  payload: data
});

export const setCustomerRequiredFields = data => ({
  type: CONSTS.SET_CUSTOMER_REQUIRED_FIELDS,
  payload: data
});

export const setConditionalFields = data => ({
  type: CONSTS.SET_CONDITIONAL_FIELDS,
  payload: data
});

export const clearPMOptions = () => ({
  type: CONSTS.CLEAR_PM_OPTIONS
});

export const clearCustomerRequiredFields = () => ({
  type: CONSTS.CLEAR_CUSTOMER_REQUIRED_FIELDS
});

export const clearCustomError = ({ key }) => ({
  type: CONSTS.CLEAR_CUSTOM_ERROR,
  payload: { key }
});

export const DownloadOrSharePaymentPdf =
  (token, useOsSharePdf, success, fail) => async () => {
    try {
      const pdfData = await API.post(`checkout/${token}/generate-pdf`, {
        language: i18n.language.replace(/_/g, '-')
      });
      const s3Link = pdfData.data?.data?.pdf_url;

      if (success) {
        success();
      }
      if (!s3Link) return;

      useOsSharePdf ? await sharePdf(s3Link) : await downloadPdf(s3Link);
    } catch (error) {
      showErrorAlert({ error });
      if (fail) {
        fail();
      }
    }
  };

const getCheckoutPageApi = async ({ token, paymentId }) => {
  const res = await API.get(
    `checkout/client/${token}${paymentId ? `?payment=${paymentId}` : ''}`
  );

  const { data } = res.data;
  localStorageService.setItem(HOSTED_PAGE_TYPES_TOKENS.checkout, data.id);

  if (paymentId) data.isDemo = true;

  return data;
};

export const getCheckoutPage =
  ({
    token,
    isRedirect,
    paymentId,
    checkoutAlreadyInUse,
    withoutLoading = false,
    withoutHandleError = false
  }) =>
  async (dispatch, getState) => {
    const handleGetPageError = (error, url = '') => {
      if (withoutHandleError) return;
      const { status, data } = parseError(error);
      dispatch(getCheckoutPageFail(status, data));
      redirectToError({ error, url });
    };

    if (!token) {
      sendGetPageFailMessage(HOSTED_PAGE_TYPES.checkout)();
      return redirectToError({ url: APP_ROUTE_NOT_FOUND });
    }

    try {
      if (!withoutLoading) {
        dispatch(displaySpinner());
      }
      dispatch(getCheckoutPageStart());

      const { isSdk, configData } = getState().global;
      const { max_create_payment_tries: maxPayments } = configData;

      let pageData;
      try {
        pageData = await getCheckoutPageApi({ token, paymentId });
      } catch (error) {
        return handleGetPageError(error, APP_ROUTE_NOT_FOUND);
      }

      const {
        status: pageStatus,
        payment,
        payments_metadata: paymentsMetadata,
        embedded
      } = pageData;
      const {
        redirect_url: redirectUrl,
        status,
        next_action: nextAction
      } = payment || {};

      const redirectToThankYou = shouldRedirectToThankYou({
        pageStatus,
        payment,
        paymentsMetadata,
        maxPayments
      });

      const { authorizeFlowRedirect, authorizeFlowError } =
        AuthorizationFlow.extractAuthorizationFlowError(pageData);

      dispatch(
        getCheckoutPageSuccess({
          data: pageData,
          redirectToThankYou,
          authorizeFlowRedirect,
          checkoutAlreadyInUse
        })
      );

      if (authorizeFlowRedirect && authorizeFlowError) {
        showErrorAlert({ error: authorizeFlowError, hide_retry: true });
      }

      if (isRedirect) {
        return;
      }

      const shouldRedirectExternal =
        redirectUrl && status === ACT_CODE && (!isSdk || embedded);
      if (shouldRedirectExternal) {
        if (embedded) {
          postKortaRedirectMessage();
        }

        if (
          isDelayedPayment({
            nextAction,
            status
          })
        ) {
          history.push(getRouteWithToken(THANK_YOU_PAGE_URL, token));
          return;
        }

        window.location.href = redirectUrl;
        return;
      }

      if (isSdk) return;
      if (redirectToThankYou) {
        history.push(getRouteWithToken(THANK_YOU_PAGE_URL, token));
      }
    } catch (error) {
      handleGetPageError(error);
    } finally {
      if (!withoutLoading) {
        dispatch(hideSpinner({ meta: { pageType: HOSTED_PAGE_TYPES.checkout } }));
      }
    }
  };

export const getCheckoutPageThankYou = (token, paymentId) => async dispatch => {
  const handleGetPageError = (error, url = '') => {
    const { status, data } = parseError(error);
    dispatch(getCheckoutPageFail(status, data));
    redirectToError({ error, url });
  };

  try {
    dispatch(getCheckoutPageStart());

    const CHANGE_LANGUAGE = false;
    let pageData;
    try {
      pageData = await getCheckoutPageApi({ token, paymentId });
    } catch (error) {
      return handleGetPageError(error, APP_ROUTE_NOT_FOUND);
    }

    dispatch(
      getCheckoutPageSuccess({ data: { ...pageData, changeLanguage: CHANGE_LANGUAGE } })
    );
  } catch (error) {
    handleGetPageError(error);
  }
};

export const authorizeAndPay =
  ({ token, paymentMethodType, customerFields }) =>
  async (dispatch, getState) => {
    const { sessionId, isMobile } = getState().global;

    const apiData = {
      type: paymentMethodType?.type,
      client_details: getClientDetails(sessionId, isMobile),
      customer_required_fields: customerFields
    };

    try {
      dispatch(authorizePaymentMethodStart());

      const response = await API.post(
        `checkout/${token}/authorize_payment_method`,
        apiData
      );

      const { redirect_url: redirectURL } = response.data.data;

      dispatch(authorizePaymentMethodSuccess());

      window.location.href = redirectURL;
    } catch (error) {
      handleSubmitPaymentError({ token, error });
      dispatch(authorizePaymentMethodFail());
    }
  };

const parseValuesToApiRequest = ({
  category,
  paymentMethodType,
  paymentMethod,
  fields,
  address,
  pmOptions,
  customerFields,
  digitalWalletData,
  valitorBuyerInfoFields
}) => {
  const { sessionId, isMobile } = store.getState().global;
  const { selectedCurrency, checkoutPage } = store.getState().checkout;
  const {
    customer,
    merchant_fields: merchantFields,
    payment,
    kwp_valitor_additional_data: kwpValitorAdditionalData
  } = checkoutPage.data;
  const {
    fixed_side: fixedSide,
    merchant_requested_currency: merchantRequestedCurrency
  } = payment || {};
  const isValitor = !isEmpty(kwpValitorAdditionalData);

  const {
    merchantPassedRequiredFields,
    merchantPassedPaymentMethodOptions,
    merchantPassedPaymentOptions
  } = getMerchantPassedFields({
    paymentMethodType: paymentMethodType?.type,
    merchantFields
  });

  const requiredFields = {
    ...fields,
    ...merchantPassedRequiredFields
  };
  const paymentMethodOptions = {
    ...pmOptions,
    ...merchantPassedPaymentMethodOptions
  };

  const mergedCustomerFields = {
    ...customerFields,
    ...merchantPassedPaymentOptions?.customer
  };

  const selectedPmt = paymentMethodType?.type;

  const apiData = {
    payment_method: !isEmpty(paymentMethod)
      ? paymentMethod
      : {
          type: selectedPmt || paymentMethodType,
          name: '',
          address: isEmpty(address) ? null : address,
          metadata: {},
          fields: requiredFields || {},
          ...(digitalWalletData && { digital_wallet: digitalWalletData })
        },
    client_details: getClientDetails(sessionId, isMobile),
    payment_method_options: paymentMethodOptions || {},
    customer,
    customer_required_fields: mergedCustomerFields,
    selected_pmt: selectedPmt,
    selected_currency: selectedCurrency,
    selected_category: category,
    cancel_fixed_side_buy: cancelFixedSideBuy({
      fixedSide,
      selectedCurrency,
      merchantRequestedCurrency
    }),
    ...(isValitor && parseValitorFields(valitorBuyerInfoFields))
  };

  return apiData;
};

const parseCardValuesToApiRequest = ({
  paymentMethodType,
  paymentMethod,
  address,
  fields,
  pmOptions,
  customerFields,
  savePaymentMethod,
  digitalWalletData,
  valitorBuyerInfoFields
}) => {
  const kortaAndValitorPaymentMethodType = [
    KORTA_PAYMENT_METHOD_TYPE_NAME,
    VALITOR_PAYMENT_METHOD_TYPE_NAME
  ];
  const { checkoutPage } = store.getState().checkout;
  const {
    kwp_additional_data: kwpKortaAdditionalData,
    kwp_valitor_additional_data: kwpValitorAdditionalData,
    payment
  } = checkoutPage?.data || {};
  const { merchant_reference_id: merchantReferenceId } = payment || {};

  const updatedFields = handleKortaPmtFields(
    paymentMethodType?.type,
    fields,
    kortaAndValitorPaymentMethodType,
    kwpKortaAdditionalData
  );
  const updatedPmOptions = addTranslationLayerPmOptions({
    pmOptions,
    kwpKortaAdditionalData,
    kwpValitorAdditionalData,
    merchantReferenceId
  });

  const apiData = {
    ...parseValuesToApiRequest({
      category: CATEGORIES.card,
      paymentMethodType,
      paymentMethod,
      address,
      fields: updatedFields,
      pmOptions: updatedPmOptions,
      customerFields,
      digitalWalletData,
      valitorBuyerInfoFields
    }),
    save_payment_method: savePaymentMethod
  };

  return apiData;
};

export const submitPaymentBase =
  ({ token, apiData, digitalWalletProvider }) =>
  async (dispatch, getState) => {
    const { isSdk, sdkParams } = getState().global;
    const { waitOnPaymentConfirmation, waitOnPaymentRedirect } = sdkParams || {};

    const response = await API.post(`checkout/${token}`, {
      ...apiData,
      sdk_wait_on_payment_redirect: waitOnPaymentRedirect
    });

    trackPaymentSuccess({ digitalWalletProvider });

    const {
      payment = {},
      hosted_page_updated_fields: hostedPageUpdatedFields = {},
      kwp_additional_data: kwpAdditionalData,
      kwp_valitor_additional_data: kwpValitorAdditionalData
    } = response.data.data;

    const { next_action: nextAction, status } = payment || {};
    const isPaymentAwaitingNextAction = isAwaitingNextAction({ nextAction, status });
    const isPaymentPending3Ds = is3DSPending({ nextAction, status });

    const allowRedirectInSdk = isSdk && waitOnPaymentRedirect && isPaymentPending3Ds;
    const shouldGoTOPendingPage =
      isPaymentAwaitingNextAction && waitOnPaymentConfirmation && !allowRedirectInSdk;

    dispatch(
      createPaymentSuccess({
        hosted_page_updated_fields: hostedPageUpdatedFields,
        payment,
        kwp_additional_data: kwpAdditionalData,
        digitalWalletProvider,
        kwp_valitor_additional_data: kwpValitorAdditionalData
      })
    );

    if (shouldGoTOPendingPage) {
      return history.push(getRouteWithToken(AWAITS_NEXT_ACTION, token));
    }
    const embedded = kwpAdditionalData?.is_embedded_view;

    setTimeout(
      () => {
        const redirected = handleRedirects({
          payment,
          supportedDeviceRedirect: !isSdk || allowRedirectInSdk || embedded,
          embedded,
          updatedFields: hostedPageUpdatedFields,
          kwpAdditionalData,
          kwpValitorAdditionalData
        });
        if (redirected) return;

        const redirectToThankYou = !isSdk || digitalWalletProvider;
        if (redirectToThankYou) {
          history.push(getRouteWithToken(THANK_YOU_PAGE_URL, token));
        }
      },
      digitalWalletProvider ? 1500 : 0
    );

    return { payment: response.data.data };
  };

export const submitPayment =
  ({
    token,
    category,
    paymentMethodType,
    paymentMethod,
    fields,
    pmOptions,
    customerFields
  }) =>
  async (dispatch, getState) => {
    try {
      const { isPreview } = getState().global;

      if (isPreview) {
        trackCategorySubmit(category);
        sendParentMessage(INCOMING_MESSAGES.ON_DEMO_CREATE_PAYMENT, { category });

        return;
      }

      if (paymentMethodType.authorization_flow && !paymentMethod) {
        return dispatch(authorizeAndPay({ token, paymentMethodType, customerFields }));
      }

      dispatch(createPaymentStart());

      const apiData = parseValuesToApiRequest({
        category,
        paymentMethodType,
        paymentMethod,
        fields,
        pmOptions,
        customerFields
      });

      return await dispatch(submitPaymentBase({ token, apiData, category }));
    } catch (error) {
      const { status } = parseError(error);
      handleSubmitPaymentError({
        token,
        error
      });
      dispatch(createPaymentFail({ error: status }));
      trackPaymentFail({ error });

      return { error: status };
    }
  };

const parseValitorFields = valitorBuyerInfoFields => ({
  customer: undefined,
  customer_required_fields: undefined,
  valitor_buyer_info_fields: valitorBuyerInfoFields
});

const handleKortaPmtFields = (
  pmt,
  fields,
  kortaAndValitorPaymentMethodType,
  kwpKortaAdditionalData
) => {
  if (!kortaAndValitorPaymentMethodType.includes(pmt)) return fields;
  if (!kwpKortaAdditionalData?.is_korta) return fields;

  const { do_param, token } = kwpKortaAdditionalData;
  if (!token || !['STORAGE', 'STNOCAP'].includes(do_param)) return fields;

  return {
    ...(fields || {}),
    merchant_custom_token: token
  };
};

const addTranslationLayerPmOptions = ({
  pmOptions,
  kwpKortaAdditionalData,
  kwpValitorAdditionalData,
  merchantReferenceId
}) => {
  if (kwpKortaAdditionalData?.is_korta) {
    return {
      ...pmOptions,
      is_korta: true,
      merchant_ref: merchantReferenceId
    };
  }

  if (!isEmpty(kwpValitorAdditionalData)) {
    return {
      ...pmOptions,
      is_valitor: true
    };
  }

  return pmOptions;
};

const handleSubmitCardPaymentPreview = fields => {
  const { number: cardNumber } = fields;

  if (cardNumber === cardDetails[CASE_TYPES.ERROR][MANDATORY_CARD_FIELDS.number]) {
    trackCardSubmit(ERROR_CODE);
    throw new Error('demo_card');
  } else if (
    cardNumber === cardDetails[CASE_TYPES.SUCCESS][MANDATORY_CARD_FIELDS.number]
  ) {
    trackCardSubmit(SUCCESS_CODE);
    sendParentMessage(INCOMING_MESSAGES.ON_DEMO_CREATE_PAYMENT, {
      category: CATEGORIES.card,
      cardType: CASE_TYPES.SUCCESS
    });
  } else if (cardNumber === cardDetails[CASE_TYPES.AUTH][MANDATORY_CARD_FIELDS.number]) {
    trackCardSubmit(ACT_CODE);
    sendParentMessage(INCOMING_MESSAGES.ON_DEMO_CREATE_PAYMENT, {
      category: CATEGORIES.card,
      cardType: CASE_TYPES.AUTH
    });
  }

  return;
};

export const submitCardPayment =
  ({
    token,
    paymentMethodType,
    paymentMethod = {},
    address = {},
    fields = {},
    pmOptions = {},
    customerFields = {},
    savePaymentMethod = false,
    digitalWallet = {},
    valitorBuyerInfoFields = {}
  }) =>
  async (dispatch, getState) => {
    const { data: digitalWalletData, provider: digitalWalletProvider } = digitalWallet;

    try {
      const { isPreview, configData } = getState().global || {};
      if (isPreview) {
        handleSubmitCardPaymentPreview(fields);
        return;
      }

      if (!digitalWalletProvider && isEmpty(paymentMethodType)) {
        store.dispatch(disableCardAbstraction());

        throw CARD_ABSTRACION_ERROR;
      }

      dispatch(createPaymentStart());

      // CLCT-849
      const { skip_luhncheck_cards: skipLuhnCheckCards } = configData || {};
      const { supported_payment_method_types: supportedPaymentMethodTypes } =
        getState().checkout?.checkoutPage?.data || {};
      const firstCardPmt = (supportedPaymentMethodTypes || []).find(
        pmtCategory => pmtCategory?.category === CATEGORIES.card
      )?.types?.[0];
      const pmtToUse = (skipLuhnCheckCards || []).includes(fields?.number)
        ? firstCardPmt
        : paymentMethodType;

      const apiData = parseCardValuesToApiRequest({
        category: CATEGORIES.card,
        paymentMethodType: pmtToUse,
        paymentMethod,
        address,
        fields,
        pmOptions,
        customerFields,
        savePaymentMethod,
        digitalWalletData,
        valitorBuyerInfoFields
      });

      return await dispatch(
        submitPaymentBase({
          token,
          apiData,
          category: CATEGORIES.card,
          digitalWalletProvider
        })
      );
    } catch (error) {
      const { status } = parseError(error);
      handleSubmitPaymentError({
        token,
        error,
        isCard: true,
        digitalWalletProvider
      });
      dispatch(createPaymentFail({ error: status, digitalWalletProvider }));
      trackPaymentFail({ error, digitalWalletProvider });

      return { error: status };
    }
  };

export const postKortaRedirectMessage = () => {
  window.parent.postMessage('3d-secure-redirect', '*');
};

export const handleRedirects = ({
  payment,
  supportedDeviceRedirect,
  embedded,
  updatedFields,
  kwpAdditionalData,
  kwpValitorAdditionalData
}) => {
  const { id: paymentId, redirect_url: redirectUrl } = payment || {};

  const shouldRedirect = redirectUrl && supportedDeviceRedirect;

  if (shouldRedirect) {
    if (updatedFields.is_demo) {
      localStorageService.setItem('demoPayment', paymentId);
    }

    if (embedded) {
      postKortaRedirectMessage();
    }
    window.location.href = redirectUrl;
    return true;
  }

  const {
    complete_checkout_auto_redirect: completeCheckoutAutoRedirect = false,
    complete_checkout_url: completeCheckoutUrl = ''
  } = updatedFields;

  if (!isEmpty(kwpAdditionalData)) {
    const shouldRedirectKortaFlow =
      supportedDeviceRedirect && completeCheckoutAutoRedirect;
    if (shouldRedirectKortaFlow) {
      if (embedded) {
        postKortaRedirectMessage();
      }
      return submitKortaForm({ kwpAdditionalData, completeCheckoutUrl });
    }
  } else if (!isEmpty(kwpValitorAdditionalData)) {
    const { valitor_payment_details: valitorPaymentDetails } =
      kwpValitorAdditionalData || {};
    const { payment_successful_url: paymentSuccessfulUrl } = valitorPaymentDetails || {};

    const shouldRedirectValitorFlow =
      supportedDeviceRedirect && paymentSuccessfulUrl && completeCheckoutAutoRedirect;
    if (shouldRedirectValitorFlow) {
      window.location.replace(paymentSuccessfulUrl);
      return true;
    }
  } else if (completeCheckoutAutoRedirect && completeCheckoutUrl) {
    window.location.href = completeCheckoutUrl;
    return true;
  }

  return false;
};

export const handleSubmitPaymentError = ({
  thankYouPath,
  token,
  error,
  isCard,
  digitalWalletProvider
}) => {
  const errorCode = error.response?.data?.status?.error_code;
  if (['REQUEST_DECLINED', 'REQUEST_DECLINED_RP'].includes(errorCode)) {
    history.push(getRouteWithToken(thankYouPath || THANK_YOU_PAGE_URL, token), {
      blocked: true
    });
  } else {
    const isCardValue =
      errorCode === 'ERROR_PAYMENT_LINK_EXCEEDS_MAX_PAYMENTS' ? false : isCard;
    showErrorAlert({ error, isCard: isCardValue, digitalWalletProvider });
  }
};

const cancelFixedSideBuy = ({ fixedSide, selectedCurrency, merchantRequestedCurrency }) =>
  fixedSide === FIXED_SIDE.BUY && selectedCurrency === merchantRequestedCurrency;

export const getRequiredFieldsByConditionalPmt = async ({
  type = '',
  pageToken = ''
}) => {
  const response = await API.get(
    `/checkout/${pageToken}/payment_method_required_fields/${type}`
  );
  return response.data?.data;
};

export const resetCheckoutStore = () => ({
  type: CONSTS.RESET_CHECKOUT_STORE
});

export const hasMerchantTriggeredSdkSubmit = shouldSubmit => ({
  type: CONSTS.HAS_MERCHANT_TRIGGERED_SDK_SUBMIT,
  payload: shouldSubmit
});
